<template>
  <div id="app" class="cursorDefault">
    <router-view />
    <!-- 全局报警弹窗 -->
    <mydetails :show.sync="alarmShow" :showData="alarmData" />
    <!-- 全局报警音效 -->
    <audio
      id="globalAlarmAudio"
      :src="require('@/assets/mp3/jb.mp3')"
      loop
    ></audio>
    <!-- 通话通知框 -->
    <VoipCallNotification />
  </div>
</template>

<script>
import appSignalR from "@/utils/appSignalRService";
import { onAnswered, onHungup, onError, onTrying, onRinging } from "@/utils/appSignalRService.voip";
import { onSecurity, onClearance } from "@/utils/appSignalRService.alarm";
import voipsignalrData from "@/utils/voipDataSignalRService";
import { getToken } from "@/utils/token";
import AudioStreamPlayer from "@/utils/audioStreamPlayer";
import mydetails from "@/components/mydetails.vue";
import Vue from "vue";
import { mapActions } from "vuex";

export default {
  name: "App",
  components: {
    mydetails,
  },
  data() {
    return {
      alarmShow: false,
      alarmData: null,
      shouldPlaySound: false, // 标志位：是否需要播放报警音效
    };
  },
  computed: {
    token() {
      return this.$store.getters.token || getToken();
    },
  },
  watch: {
    token: {
      handler(val) {
        if (val) {
          this.initSignalR();
        } else {
          appSignalR.stop();
        }
      },
      immediate: true,
    },
    /**
     * 监听报警弹窗状态，控制报警音效播放
     */
    alarmShow(val) {
      const audio = document.getElementById("globalAlarmAudio");
      if (audio) {
        if (val && this.shouldPlaySound) {
          audio.play().catch((err) => {
            console.warn("音频播放失败 (可能由于浏览器限制):", err);
          });
        } else {
          audio.pause();
          audio.currentTime = 0;
          // 弹窗关闭时重置音效标志位
          if (!val) {
            this.shouldPlaySound = false;
          }
        }
      }
    },
  },
  created() {
    // 暴露全局方法供子组件调用
    // data: 报警数据, playSound: 是否播放音效 (默认不播放，仅推送触发时启用)
    Vue.prototype.$showAlarm = (data, playSound = false) => {
      this.alarmData = data;
      this.shouldPlaySound = playSound;
      this.alarmShow = true;
      // 如果需要播放，显式触发
      if (playSound) {
        this.$nextTick(() => {
          const audio = document.getElementById("globalAlarmAudio");
          if (audio && audio.paused) {
            audio.play().catch((err) => console.warn("音频播放失败:", err));
          }
        });
      }
    };
  },
  beforeDestroy() {
    // 组件销毁时移除特定监听器，不影响其他组件
    if (this.securityCallback) {
      signalr.off("security", this.securityCallback);
    }
    if (this.clearanceCallback) {
      signalr.off("Clearance", this.clearanceCallback);
    }
  },
  methods: {
    ...mapActions({
      setCurrentCallPhone: "windState/setCurrentCallPhone",
      clearCurrentCallPhone: "windState/clearCurrentCallPhone",
    }),
    async initSignalR() {
      // 全局初始化 SignalR
      // 默认使用 VOIP 的 URL，因为报警通知现在合并到此服务中
      await appSignalR.init();
      this.initVoipListeners();
      this.onSecurity();
      this.onClearance();
    },
    async initVoipListeners() {
      // 全局初始化 VOIP 监听器
      onError((error) => {
        console.error("SignalR 连接失败:", error);
        this.$message.error(error);
      });
      onAnswered(() => {
        // 通话已开始，启动麦克风捕获
        AudioStreamPlayer.startMicCapture(voipsignalrData.connection);
      });
      onTrying(async () => {
        await voipsignalrData.init();
        await voipsignalrData.Connect(appSignalR.connection.connectionId);
        voipsignalrData.receiveAudioStream((encodedAudio) =>
          AudioStreamPlayer.playChunk(encodedAudio),
        );
      });
      onRinging(() => {
        // 响铃事件，电话号码由 VoipCallNotification 组件在 trying 时设置
      });
      onHungup(() => {
        voipsignalrData.stopReceiveAudioStream();
        AudioStreamPlayer.stopMicCapture();
        voipsignalrData.stop();
        this.clearCurrentCallPhone();
        console.log("Call ended, releasing microphone resources...");
      });
    },
    onSecurity() {
      // 使用命名函数以便精确移除监听器
      this.securityCallback = (message) => {
        console.log("全局收到安全推送:", message);
        // 解析 remark 获取 status（船舶检测）
        let status = 0;
        try {
          const remark = typeof message.remark === "string" ? JSON.parse(message.remark) : message.remark;
          status = remark?.state ?? 0;
        } catch {
          status = 0;
        }
        // 标准化推送数据
        const standardData = {
          ...message,
          alarmTime:
            message.alarmTime || message.recordTime || new Date().toISOString(),
          deviceType: 9, // 安全报警
          alarmType: message.messageTypeStr || message.alarmType || "安全报警",
          alarmLevel: message.alarmLevel || 1,
          messageType: message.messageType || 2,
        };
        // status=1（异常）才弹窗+音效
        if (status === 1) {
          this.$showAlarm(standardData, true);
        }
      };
      onSecurity(this.securityCallback);
    },
    onClearance() {
      // 使用命名函数以便精确移除监听器
      this.clearanceCallback = (message) => {
        console.log("全局收到净空推送:", message);
        // 如果收到报警(2)或预警(1)数据，触发全局弹窗提示
        if (message && (message.alertType === 1 || message.alertType === 2)) {
          this.$showAlarm(
            {
              ...message,
              alarmTime:
                message.recordTime ||
                message.alertTime ||
                new Date().toISOString(),
              deviceName: message.aerogeneratorNumber,
              alarmType: message.alertType === 1 ? "净空预警" : "净空报警",
              messageType: message.alertType,
              alarmLevel: message.alertType === 1 ? 3 : 1, // 预警3级(黄)，报警1级(红)
              deviceType: 5, // 净空设备
              videoUrl: message.markImagePath || message.MarkImagePath, // 净空报警视频路径
            },
            true,
          );
        }
      };
      onClearance(this.clearanceCallback);
    },
  },
};
</script>

<style lang="scss">
#app {
  width: 100%;
  height: 100%;
}

* {
  padding: 0;
  margin: 0;
}
</style>
